import React, { createContext, useCallback, useEffect, useState } from 'react'
import qs from 'query-string'
import { useLocation } from '@reach/router'
import { useEventListener } from '@superrb/gatsby-addons/hooks'

export const FilterContext = createContext({
  selectedTags: [],
  availableTags: [],
  expanded: true,
  addTag: tag => {},
  removeTag: tag => {},
  setAvailableTags: tags => tags,
  setExpanded: expanded => {},
  toggleExpanded: expanded => {},
  listingOpacity: 1,
  setListingOpacity: opacity => {},
})

const QS_OPTIONS = { arrayFormat: 'bracket' }

const pushState = (state, title, url) =>
  window.history.pushState(state, title, url)

export const FilterContextProvider = ({ children }) => {
  const [queryString, setQueryString] = useState('')
  const [selectedTags, setSelectedTagsState] = useState([])
  const [availableTags, setAvailableTagsState] = useState([])
  const [expanded, setExpanded] = useState(false)
  const [listingOpacity, setListingOpacity] = useState(1)
  const location = useLocation()

  const setSelectedTags = useCallback(
    callback => {
      setSelectedTagsState(tags => {
        if (Array.isArray(callback)) {
          tags = callback
        } else {
          tags = callback(tags)
        }

        const set = new Set(tags)
        return [...set]
      })
    },
    [setSelectedTagsState],
  )

  const setAvailableTags = useCallback(
    callback => {
      setAvailableTagsState(tags => {
        if (Array.isArray(callback)) {
          tags = callback
        } else {
          tags = callback(tags)
        }

        const set = new Set(tags)
        return [...set]
      })
    },
    [setAvailableTagsState],
  )

  const updateTags = useCallback(() => {
    const newQueryString = qs.parse(location.search, QS_OPTIONS)
    setQueryString(newQueryString)
    setSelectedTags(newQueryString.tags || [])
  }, [location, setQueryString, setSelectedTags])

  const setSelectedTagsAndUpdateUrl = useCallback(
    callback => {
      setSelectedTags(tags => {
        const newTags = callback(tags)
        const newQueryString = queryString || {}
        newQueryString.tags = [...newTags]

        pushState(
          newQueryString,
          null,
          `?${qs.stringify(newQueryString, QS_OPTIONS)}`,
        )

        return newTags
      })
    },
    [queryString, setSelectedTags],
  )

  const addTag = useCallback(
    tag => {
      setSelectedTagsAndUpdateUrl(tags => {
        tags.push(tag)
        return tags
      })
    },
    [setSelectedTagsAndUpdateUrl],
  )

  const removeTag = useCallback(
    tag => {
      setSelectedTagsAndUpdateUrl(tags => {
        tags.splice(tags.indexOf(tag), 1)
        return tags
      })
    },
    [setSelectedTagsAndUpdateUrl],
  )

  const toggleExpanded = useCallback(() => {
    setExpanded(expanded => !expanded)
  }, [setExpanded])

  useEffect(() => {
    const newQueryString = qs.parse(location.search, QS_OPTIONS)
    setQueryString(newQueryString)
    setSelectedTags(newQueryString.tags || [])
  }, [location, setQueryString, setSelectedTags])

  useEventListener(
    'popstate',
    updateTags,
    {},
    typeof window !== 'undefined' ? window : undefined,
  )

  return (
    <FilterContext.Provider
      value={{
        selectedTags,
        availableTags,
        expanded,
        addTag,
        removeTag,
        setAvailableTags,
        setExpanded,
        toggleExpanded,
        listingOpacity,
        setListingOpacity,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}
